import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, throwError, of, forkJoin } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { Warehouse } from '../interfaces/Warehouses.interfaces';
import { BorderoGroup } from '../interfaces/LoadingList.interfaces';

// =============================================
// ENUMS
// =============================================

export enum ParcelStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  IN_TRANSIT = 'IN_TRANSIT',
  DELIVERED = 'DELIVERED',
  RETURNED = 'RETURNED',
  CANCELLED = 'CANCELLED',
  DAMAGED = 'DAMAGED',
  LOST = 'LOST',
  WAITING = 'WAITING',
  PROBLEM = 'PROBLEM',
  PREPARED = 'Preparata',
  CLOSED = 'Chiuso',
  IN_TRIP_TO_TP = 'In viaggio verso TP',
  IN_TRIP = 'In viaggio'
}

export enum WaybillStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  PENDING = 'PENDING',
  IN_TRANSIT = 'IN_TRANSIT',
  DELIVERED = 'DELIVERED',
  RETURNED = 'RETURNED',
  CANCELLED = 'CANCELLED',
  PROBLEM = 'PROBLEM'
}

export enum WaybillDeliveryMethod {
  STANDARD = 'STANDARD',
  EXPRESS = 'EXPRESS',
  OVERNIGHT = 'OVERNIGHT',
  SAME_DAY = 'SAME_DAY',
  PICKUP = 'PICKUP'
}

export enum WaybillDeliveryType {
  DOOR_TO_DOOR = 'DOOR_TO_DOOR',
  DOOR_TO_PORT = 'DOOR_TO_PORT',
  PORT_TO_DOOR = 'PORT_TO_DOOR',
  PORT_TO_PORT = 'PORT_TO_PORT'
}

export enum DealerStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED',
  PENDING = 'PENDING',
  TERMINATED = 'TERMINATED'
}

export enum BrandStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  SUSPENDED = 'SUSPENDED'
}

// =============================================
// INTERFACES
// =============================================

export interface Parcel {
  parcelId?: number;
  parcelCode: string;
  parcelWaybillCode?: string;
  parcelPalletCode?: string;
  parcelShipmentCode?: string;
  parcelBrandCode: string;
  parcelDealerCode?: string;
  parcelBarcode?: string;
  parcelTrackingNumber?: string;
  parcelDate?: string;
  parcelDeliveryDate?: string;
  parcelEstimatedDeliveryDate?: string;
  parcelOriginalDeliveryDate?: string;
  parcelLastEventDate?: string;
  parcelTakeInChargeDate?: string;
  parcelLastUpdateAt?: string;
  parcelUpdatedAt?: string;
  parcelCreatedAt?: string;
  parcelDeliveryStatus?: string;
  parcelDeliveryMethod?: string;
  parcelDeliveryType?: string;
  parcelDeliveryMaterial?: string;
  parcelDeliveryReason?: string;
  parcelTakeInChargeReason?: string;
  parcelDescription?: string;
  parcelWeight?: number;
  parcelVolume?: number;
  parcelLength?: number;
  parcelWidth?: number;
  parcelHeight?: number;
  parcelType?: string;
  parcelLocation?: string;
  parcelLatitude?: number;
  parcelLongitude?: number;
  parcelOriginalLatitude?: number;
  parcelOriginalLongitude?: number;
  parcelFragile?: string;
  parcelHazardousMaterial?: string;
  parcelTemperatureSensitive?: string;
  parcelHighValueItem?: string;
  parcelRestrictedItem?: string;
  parcelCustomsClearanceRequired?: string;
  parcelDeliveryDateIsForced?: string;
  parcelDeliveryDateIsUpdated?: string;
  parcelDeliveryCoordinatesIsForced?: string;
  parcelDeliveryCoordinatesIsUpdated?: string;
  parcelInsuredValue?: number;
  parcelStatus?: ParcelStatus;
  parcelVersion?: number;
  parcelLastUpdateBy?: string;
  parcelUpdatedBy?: string;
  parcelClientCode?: string;
  parcelDetailStatus?: string;
  parcelLoadingList?: string;
}

export interface Waybill {
  waybillId?: number;
  waybillCode: string;
  waybillPalletCode?: string;
  waybillShipmentCode: string;
  waybillTrackingNumber?: string;
  waybillDate?: string;
  waybillEstimatedDeliveryDate?: string;
  waybillActualDeliveryDate?: string;
  waybillOriginalDeliveryDate?: string;
  waybillCreatedAt?: string;
  waybillUpdatedAt?: string;
  waybillDeliveryMethod?: WaybillDeliveryMethod;
  waybillDeliveryType?: WaybillDeliveryType;
  waybillTransitTimeHours?: number;
  waybillOriginLocation?: string;
  waybillDestinationLocation?: string;
  waybillLastKnownLocation?: string;
  waybillLatitude?: number;
  waybillLongitude?: number;
  waybillOriginalLatitude?: number;
  waybillOriginalLongitude?: number;
  waybillTotalWeight?: number;
  waybillTotalVolume?: number;
  waybillFragile?: boolean;
  waybillHazardousMaterial?: boolean;
  waybillCustomsClearanceRequired?: boolean;
  waybillBrandCode?: string;
  waybillDealerCode?: string;
  waybillCarrierCode?: string;
  waybillStatus?: WaybillStatus;
  waybillUpdatedBy?: string;
  waybillNote?: string;
  waybillFile?: string;
  waybillVersion?: number;
  waybillClientCode?: string;
  waybillLoadingList?: string;
}

export interface Dealer {
  dealerId?: number;
  dealerCode: string;
  dealerName?: string;
  dealerLegalName?: string;
  dealerLegalCode?: string;
  dealerVatNumber?: string;
  dealerInternalBillingId?: string;
  dealerInternalId?: string;
  dealerSdiCode?: string;
  dealerDeliveryZoneCode?: string;
  dealerBillingZoneCode?: string;
  dealerLegalAddressStreet?: string;
  dealerLegalAddressNumber?: string;
  dealerLegalAddressZip?: string;
  dealerLegalAddressCity?: string;
  dealerLegalAddressProvince?: string;
  dealerLegalAddressCountry?: string;
  dealerOperatingAddressStreet?: string;
  dealerOperatingAddressNumber?: string;
  dealerOperatingAddressZip?: string;
  dealerOperatingAddressCity?: string;
  dealerOperatingAddressProvince?: string;
  dealerOperatingAddressCountry?: string;
  dealerIncorporationDate?: string;
  dealerIndustry?: string;
  dealerStatus?: DealerStatus;
  dealerNotes?: string;
  dealerLatitude?: number;
  dealerLongitude?: number;
  dealerCoordinatesOrigin?: string;
  dealerVersion?: number;
  dealerCreatedAt?: string;
  dealerClientCode?: string;
  dealerBrandCode?: string;
}


export interface Brand {
  brandCode: string;
  brandName?: string;
  brandAcronym?: string;
}


/**
 * Configurazione di un singolo campo lato frontend, fornita dal backend
 * tramite l'oggetto Client (clientFieldSettings). Per default tutti i campi
 * sono abilitati: questo settaggio serve solo a disabilitare quelli non attivi
 * e (opzionalmente) a indicare un placeholder personalizzato.
 */
export interface ClientFieldSetting {
  enabled: boolean;
  placeholder?: string;
  required?: boolean;
}

/**
 * Settaggio dei campi disponibile per un Client.
 * Le chiavi corrispondono ai nomi dei FormControl effettivamente presenti
 * nel form di richiesta spedizione (parcels) e nella popup di inserimento
 * distributivo. Chiavi attualmente gestite:
 *   - waybillCode        (campo "Bolla")
 *   - parcelDescription  (descrizione del collo)
 *   - parcelWeight       (peso del singolo collo)
 *   - parcelVolume       (volume del singolo collo)
 *   - parcelDimensions   (lunghezza/larghezza/altezza del singolo collo)
 *   - parcelCount        (numero colli, nella popup distributiva)
 *   - totalWeight        (peso totale, nella popup distributiva)
 *   - totalVolume        (volume totale, nella popup distributiva)
 */
export interface ClientFieldSettings {
  version?: string;
  type?: string;
  /**
   * Orario oltre il quale non è più possibile inserire una richiesta di
   * spedizione per la giornata odierna. Formati supportati:
   *  - numero (ora intera, es. `17` → 17:00)
   *  - stringa `"HH"` (es. `"17"` → 17:00)
   *  - stringa `"HH:mm"` (es. `"17:30"`)
   * Se assente o non valido, fallback a `17:00`.
   */
  max_date_insert?: string | number;
  'loading-list'?: boolean;
  fields: { [fieldName: string]: ClientFieldSetting };
}


export interface ClientsBrands {
  clientCode: string;
  clientName?: string;
  brands: Brand[];
  /**
   * Settaggio dei campi disponibile per il client (fornito dal backend
   * dentro `userFunctions.clients[*]`). Vedi {@link ClientFieldSettings}.
   */
  clientFieldSettings?: ClientFieldSettings;
}

export interface Client {
  clientId?: number;
  clientCode: string;
  clientName?: string;
  clientStatus?: BrandStatus;
  clientVatNumber?: string;
  clientMaximumRange?: number;
  /**
   * Settaggio dei campi disponibile per il client (fornito dal backend).
   * Vedi {@link ClientFieldSettings} per la struttura attesa.
   */
  clientFieldSettings?: ClientFieldSettings;
}

// Interface per i parametri di ricerca
export interface SearchParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  dateFrom?: string;
  dateTo?: string;
  dealerCode?: string;
  brandCode?: string;
}

// Interface per ShipmentRequestData (composta)
export interface Shipment {
  waybill: Waybill;
  parcels: Parcel[];
}

// Riepilogo colli della giornata (endpoint POST /shipment-request/summary)
export interface ParcelsSummary {
  distinctWaybills: number;
  totalParcels: number;
  totalWeight: number;
  totalVolume: number;
  totalInsuredValue: number;
}

/** Risposta di POST /shipment-request/insert-data-by-file */
export interface InsertDataByFileResponse {
  status: string;
  receivedCharacters: number;
  receivedAt: string;
  userCode: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ShipmentRequestService {
  private apiUrl = environment.apiUrl + '/shipment-request';
  private token = this.authService.getAccessToken();
  private language = localStorage.getItem('language');
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.token}`,
      'Accept-language': this.language ?? 'it'
    })
  };

  /**
   * Opzioni per gli endpoint che accettano testo puro.
   * responseType 'json' esplicito: senza, Angular deduce 'text' dal Content-Type
   * della richiesta e non deserializza la risposta.
   */
  private textPlainHttpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'text/plain',
      'Authorization': `Bearer ${this.token}`,
      'Accept-language': this.language ?? 'it'
    }),
    responseType: 'json' as const
  };

  constructor(private http: HttpClient, private authService: AuthService) { }

  // =============================================
  // SHIPMENT CRUD OPERATIONS
  // =============================================

  /**
   * Invia a /insert-data-by-file il contenuto di un file come stringa (text/plain).
   * Richiede il permesso CREATE sulla funzione "shipment-request".
   * Il backend risponde con status, receivedCharacters, receivedAt e userCode.
   */
  insertDataByFile(fileContent: string): Observable<InsertDataByFileResponse> {
    return this.http
      .post<InsertDataByFileResponse>(`${this.apiUrl}/insert-data-by-file`, fileContent, this.textPlainHttpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Recupera la configurazione campi per un client specifico.
   * Endpoint dedicato separato dal login.
   */
  getClientFieldSettings(clientCode: string): Observable<ClientFieldSettings | null> {
    return this.http
      .get<ClientFieldSettings>(`${environment.apiUrl}/shipment-request/clients/${clientCode}/field-settings`, this.httpOptions)
      .pipe(
        catchError((error) => {
          // Se non presente configurazione o endpoint non disponibile,
          // non blocchiamo la UI: fallback ai default locali.
          if (error?.status === 404 || error?.status === 204) {
            return of(null);
          }
          return of(null);
        })
      );
  }

  /**
   * Recupera i dati anagrafici di un singolo magazzino tramite il suo codice.
   * Usato, ad esempio, per popolare l'anagrafica mittente sulle etichette.
   */
  getWarehouseDetails(warehouseCode: string): Observable<Warehouse> {
    return this.http
      .get<Warehouse>(`${this.apiUrl}/warehouse-details/${warehouseCode}`, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Recupera tutte le spedizioni
   */
  getShipments(): Observable<Shipment[]> {
    return this.http.get<Shipment[]>(`${this.apiUrl}/shipments`, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Recupera una spedizione specifica per ID
   */
  getShipment(id: string): Observable<Shipment> {
    return this.http.get<Shipment>(`${this.apiUrl}/shipments/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Crea una nuova spedizione
   */
  createShipment(shipment: Shipment): Observable<Shipment> {
    return this.http.post<Shipment>(`${this.apiUrl}/create`, shipment, this.httpOptions)
      .pipe(
        catchError(this.handleErrorWithMessage)
      );
  }

  /**
   * Aggiorna una spedizione esistente
   */
  updateShipment(id: string, shipmentCode: string, shipment: Shipment): Observable<Shipment> {
    console.log("shipment : ", shipment);
    // Nessun handleError: il chiamante mostra il messaggio del backend (es. error.tour.routing.inactive)
    return this.http.put<Shipment>(`${this.apiUrl}/shipments/${id}/${shipmentCode}`, shipment, this.httpOptions);
  }

  // =============================================
  // WAYBILL CRUD OPERATIONS
  // =============================================

  /**
   * Recupera tutte le bolle (solo da oggi in poi, senza filtri)
   */
  getWaybills(clientsBrands: ClientsBrands[]): Observable<Waybill[]> {
    return this.http.post<Waybill[]>(`${this.apiUrl}/waybills`, clientsBrands, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Recupera una bolla specifica per ID
   */
  getWaybill(id: string): Observable<Waybill> {
    return this.http.get<Waybill>(`${this.apiUrl}/waybills/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Crea una nuova bolla
   */
  createWaybill(waybill: Waybill): Observable<Waybill> {
    return this.http.post<Waybill>(`${this.apiUrl}/waybills`, waybill, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Aggiorna una bolla esistente
   */
  updateWaybill(id: string, waybill: Waybill): Observable<Waybill> {
    return this.http.put<Waybill>(`${this.apiUrl}/waybills/${id}`, waybill, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Elimina una bolla coi parcels associati e se singola nello shipment elimina anche lo shipment
   */  deleteWaybill(id: string, shipmentCode: string, clients: ClientsBrands[]): Observable<void> {
    return this.http.request<void>('delete', `${this.apiUrl}/waybill/${id}/${shipmentCode}`, {
      body: clients,
      responseType: 'text' as 'json'
    }).pipe(
      map(() => void 0), // Convert any response to void
      catchError(this.handleError)
    );
  }
  // =============================================
  // PARCEL CRUD OPERATIONS
  // =============================================

  /**
   * Recupera tutti i colli (senza filtri, solo quelli da oggi in poi)
   */
  getParcels(clientsBrands: ClientsBrands[]): Observable<Parcel[]> {
    return this.http.post<Parcel[]>(`${this.apiUrl}/parcels`, clientsBrands, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Recupera il riepilogo dei colli della giornata odierna (bolle distinte,
   * numero colli, peso/volume/valore assicurato totali) per i client/brand indicati.
   */
  getParcelsSummary(clientsBrands: ClientsBrands[]): Observable<ParcelsSummary> {
    return this.http.post<ParcelsSummary>(`${this.apiUrl}/summary`, clientsBrands, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }
  /**
   * Recupera un collo specifico per ID
   */
  getParcel(id: string): Observable<Parcel> {
    return this.http.get<Parcel>(`${this.apiUrl}/parcels/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Recupera tutti i colli di una bolla specifica
   */
  getParcelsByWaybill(waybillId: string): Observable<Parcel[]> {
    return this.http.get<Parcel[]>(`${this.apiUrl}/waybills/${waybillId}/parcels`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Crea un nuovo collo
   */
  createParcel(parcel: Parcel): Observable<Parcel> {
    return this.http.post<Parcel>(`${this.apiUrl}/parcels`, parcel, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Crea più colli in batch
   */
  createParcels(parcels: Parcel[]): Observable<Parcel[]> {
    return this.http.post<Parcel[]>(`${this.apiUrl}/parcels/batch`, { parcels }, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Aggiorna un collo esistente
   */
  updateParcel(id: string, parcel: Parcel): Observable<Parcel> {
    return this.http.put<Parcel>(`${this.apiUrl}/parcels/${id}`, parcel, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Elimina un collo
   */  deleteParcel(id: string): Observable<void> {
    console.log('Deleting parcel with id:', id);
    return this.http.delete(`${this.apiUrl}/parcels/${id}`, {
      responseType: 'text'
    })
      .pipe(
        map(() => void 0), // Convert any response to void
        catchError(this.handleError)
      );
  }// =============================================
  // DEALER CRUD OPERATIONS
  // =============================================

  /**
   * Recupera tutti i dealer (POST con clientsBrands)
   */
  getDealers(clientsBrands: ClientsBrands[]): Observable<Dealer[]> {
    return this.http.post<Dealer[]>(`${this.apiUrl}/dealers`, clientsBrands, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Recupera un dealer specifico per ID
   */
  getDealer(id: string): Observable<Dealer> {
    return this.http.get<Dealer>(`${this.apiUrl}/dealers/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Crea un nuovo dealer
   */
  createDealer(dealer: Dealer): Observable<Dealer> {
    return this.http.post<Dealer>(`${this.apiUrl}/insert-dealer`, dealer, this.httpOptions)
      .pipe(
        catchError(this.handleErrorWithMessage)
      );
  }

  /**
   * Aggiorna un dealer esistente
   */
  updateDealer(dealer: Dealer): Observable<Dealer> {
    return this.http.put<Dealer>(`${this.apiUrl}/update-dealer/${dealer.dealerCode}`, dealer, this.httpOptions)
      .pipe(
        catchError(this.handleErrorWithMessage)
      );
  }

  /**
   * Cerca dealer per nome o codice
   */
  searchDealers(query: string, limit: number = 10): Observable<Dealer[]> {
    const params = new HttpParams()
      .set('search', query)
      .set('limit', limit.toString());

    return this.http.get<Dealer[]>(`${this.apiUrl}/dealers/search`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  // =============================================
  // BRAND READ OPERATIONS (READ-ONLY)
  // =============================================

  /**
   * Recupera tutti i brand
   */
  getBrands(): Observable<Brand[]> {
    return this.http.get<Brand[]>(`${this.apiUrl}/brands`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // =============================================
  // WAREHOUSE READ OPERATIONS (READ-ONLY)
  // =============================================

  /**
   * Recupera i borderò di oggi raggruppati per magazzino destinazione.
   *
   * @param clientsBrands Clienti e marchi selezionati
   * @param splitByClientZones Se true chiede il raggruppamento per zone del
   *   cliente. Viaggia come header perché il body è già occupato dalla lista
   *   dei clienti (stessa scelta di createLoadingListFromComParcelIds); il
   *   backend lo tratta come opzionale con default false.
   */
  getLoadingList(clientsBrands: ClientsBrands[], splitByClientZones: boolean = false): Observable<BorderoGroup[]> {
    const headers = this.httpOptions.headers
      .set('X-Split-By-Client-Zones', String(splitByClientZones));
    return this.http.post<BorderoGroup[]>(`${this.apiUrl}/loading-list`, clientsBrands, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Crea il borderò di un gruppo della lista.
   *
   * Il backend non usa gli id già presenti in pagina: rilegge i colli a
   * database, così prende anche quelli arrivati nel frattempo. Per questo
   * vanno passate anche la divisione per zone usata per caricare la lista e la
   * zona del gruppo su cui si sta creando: senza, la rilettura riprenderebbe
   * tutti i colli del magazzino e della data, comprese le altre zone.
   *
   * @param splitByClientZones lo stesso valore passato a getLoadingList
   * @param clientZone zona del gruppo; assente è il gruppo dei concessionari
   *   senza zona configurata, che non è la stessa cosa di "non filtrare"
   */
  createLoadingListFromComParcelIds(
    warehouseFrom: string,
    warehouseTo: string,
    parcelDate: string,
    clientCodes: string[],
    brandCodes: string[],
    splitByClientZones: boolean = false,
    clientZone?: string
  ): Observable<any> {
    let headers = this.httpOptions.headers
      .set('X-Warehouse-From', warehouseFrom)
      .set('X-Warehouse-To', warehouseTo)
      .set('X-Loading-list-date', parcelDate)
      .set('X-Client-Code', clientCodes.join(','))
      .set('X-Brand-Code', brandCodes.join(','))
      .set('X-Split-By-Client-Zones', String(splitByClientZones));

    if (clientZone) {
      headers = headers.set('X-Client-Zone', clientZone);
    }

    return this.http.post<any>(`${this.apiUrl}/createLoadingListFromComParcelIds`, {}, { headers })
      .pipe(
        catchError(this.handleError)
      );
  }

  printLoadingList(loadingListCode: string, clientsBrands: ClientsBrands[]): Observable<Blob> {
    const headers = this.httpOptions.headers.set('loadingListCode', loadingListCode);
    console.log('[printLoadingList] Headers inviati:', headers.keys().reduce((acc, k) => ({ ...acc, [k]: headers.get(k) }), {}));
    return this.http.post(
      `${this.apiUrl}/print-loading-list`,
      clientsBrands,
      { headers, responseType: 'blob' }
    ).pipe(
      catchError(this.handleError)
    );
  }

  /**
   * Recupera tutti i magazzini (POST con clientsBrands)
   */
  getWarehouses(clientsBrands: ClientsBrands[]): Observable<Warehouse[]> {
    return this.http.post<Warehouse[]>(`${this.apiUrl}/warehouses`, clientsBrands, this.httpOptions)
      .pipe(
        catchError(this.handleError)
      );
  }


  // =============================================
  // ERROR HANDLING
  // =============================================

  /**
   * Gestione centralizzata degli errori
   */
  private handleError(error: any): Observable<never> {
    let errorMessage = 'Si è verificato un errore sconosciuto';
    console.error('Dettagli errore:', errorMessage);
    console.log('Errore dettagliato:', error);
    if (error.error instanceof ErrorEvent) {
      // Errore lato client
      errorMessage = `Errore: ${error.error.message}`;
    } else {
      // Errore lato server
      switch (error.status) {
        case 400:
          errorMessage = 'Richiesta non valida';
          break;
        case 401:
          errorMessage = 'Non autorizzato';
          break;
        case 403:
          errorMessage = 'Accesso negato';
          break;
        case 404:
          errorMessage = 'Risorsa non trovata';
          break;
        case 422:
          errorMessage = error.error?.message || 'Dati non validi';
          break;
        case 500:
          errorMessage = 'Errore interno del server';
          break;
        default:
          errorMessage = `Errore ${error.status}: ${error.error?.message || error.statusText}`;
      }
    }
    

    console.error('Errore HTTP:', error);
    return throwError(() => new Error(JSON.stringify(error.error)+", "+JSON.stringify(error.error.details)));
  }

  private handleErrorWithMessage(error: any): Observable<never> {
    const code = error?.error?.code ?? null;
    const backendMessage =
      error?.error?.message ||
      error?.error?.error ||
      error?.message ||
      'Si è verificato un errore sconosciuto';
    console.error(`Errore backend [${code ?? 'N/A'}]: ${backendMessage}`, error);
    return throwError(() => new Error(backendMessage));
  }
}
